import { Group, Paper, SimpleGrid, Text } from '@mantine/core';
import { IconBriefcase, IconCoin, IconReceipt2, IconShoppingCart } from '@tabler/icons-react';
import type { PartnerStats as PartnerStatsData } from '@ayocore/erp/crm/server/repositories/PartnerRepository';
import classes from './PartnerStats.module.css';

type StatItem = {
  label: string;
  total: number;
  count?: number;
  icon?: keyof typeof icons;
  tone: keyof typeof titleTones;
};

const formatMoney = (value: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value);

const icons = {
  sell: IconReceipt2,
  pay: IconCoin,
  order: IconShoppingCart,
  deals: IconBriefcase,
};

const titleTones = {
  sell: 'titleSell',
  pay: 'titlePay',
  order: 'titleOrder',
  deals: 'titleDeals',
  debt: 'titleDebt',
} as const;

export function PartnerStats({ stats }: { stats: PartnerStatsData | null }) {
  const safeStats: PartnerStatsData = stats ?? {
    documents: {
      sell: { count: 0, total: 0 },
      pay: { count: 0, total: 0 },
      order: { count: 0, total: 0 },
    },
    deals: { count: 0, total: 0 },
  };

  const debtValue = safeStats.documents.sell.total - safeStats.documents.pay.total;

  const items: StatItem[] = [
    {
      label: 'Отгрузки',
      total: safeStats.documents.sell.total,
      count: safeStats.documents.sell.count,
      icon: 'sell',
      tone: 'sell',
    },
    {
      label: 'Оплаты',
      total: safeStats.documents.pay.total,
      count: safeStats.documents.pay.count,
      icon: 'pay',
      tone: 'pay',
    },
    {
      label: 'Заказы 1С',
      total: safeStats.documents.order.total,
      count: safeStats.documents.order.count,
      icon: 'order',
      tone: 'order',
    },
    {
      label: 'Сделки',
      total: safeStats.deals.total,
      count: safeStats.deals.count,
      icon: 'deals',
      tone: 'deals',
    },
    {
      label: debtValue < 0 ? 'Мы должны' : 'Нам должны',
      total: debtValue,
      tone: 'debt',
    },
  ];

  return (
    <div className={classes.root}>
      <SimpleGrid cols={{ base: 1, xs: 2, md: 5 }}>
        {items.map(item => {
          const Icon = item.icon ? icons[item.icon] : null;
          return (
            <Paper withBorder p='xs' radius='md' shadow='none' key={item.label}>
              <Group justify='space-between'>
                <Text size='xs' className={`${classes.title} ${classes[titleTones[item.tone]]}`}>
                  {item.label}
                  {item.count !== undefined ? ` (${item.count})` : ''}
                </Text>
                {Icon ? <Icon className={classes.icon} size={14} stroke={1.5} /> : null}
              </Group>

              <Group align='center' gap='xs' mt={8}>
                <Text className={classes.value}>{formatMoney(item.total)} ₽</Text>
              </Group>
            </Paper>
          );
        })}
      </SimpleGrid>
    </div>
  );
}
